import type { Company } from '@/api';

export const useCompanyPolicy = (model: Ref<Company | undefined>) => {
    const { company, user, permissions, isSuperAdmin, isStaff, authority } = useCompany();
    const getPolicy = (model: Company | undefined) => {
        const toggleBankDetailsLock = isStaff.value && permissions.value.toggleBankDetailsLock;
        const updateBankDetails =
            model != null &&
            company.value?.id === model.id &&
            (permissions.value.editBankDetails || isSuperAdmin.value);

        const sendPlatformAgreement = model != null && isStaff.value && permissions.value.manageAgreements;

        const view =
            model != null &&
            (company.value?.id === model.id ||
                (model.company_type === 'agency' && permissions.value.viewAgencies) ||
                (model.company_type === 'school' && permissions.value.viewSchools) ||
                (model.company_type === 'student' && permissions.value.viewStudents) ||
                model.handlers?.some(h => h.user_id === user.value?.id));

        const update =
            model != null &&
            ((company.value?.id === model.id && isSuperAdmin.value) ||
                (isStaff.value &&
                    ((permissions.value.manageAgencies && model.company_type === 'agency') ||
                        (permissions.value.manageSchools && model.company_type === 'school') ||
                        (permissions.value.manageStudents && model.company_type === 'student') ||
                        model.handlers?.some(h => h.user_id === user.value?.id))));

        const updateLogo = model != null && update;

        const updateAcceptingPartnerRequests = update && permissions.value.editPartners;

        const show = {
            financial:
                (model != null && company.value?.id === model.id && permissions.value.editBankDetails) || isStaff.value,
            branches: isStaff.value || (model != null && company.value?.id === model.id),
            partners:
                (model?.company_type === 'agency' || model?.company_type === 'school') &&
                (permissions.value.viewPartners || permissions.value.viewAgreements),
            structure: isStaff.value || isSuperAdmin.value || permissions.value.editRoles,
        };

        const updateAssessmentLevel =
            model != null &&
            isStaff.value &&
            authority.overCompany(model) &&
            permissions.value.editCompanies &&
            model.company_type === 'school';

        const updateCriticalDetails = permissions.value.editCompanyCriticalDetails && update;

        const createBranch =
            model != null &&
            model.id === company.value?.id &&
            ((model.company_type === 'staff' && permissions.value.createBranch) ||
                (model.company_type === 'agency' && permissions.value.createAgencyBranch) ||
                (model.company_type === 'school' && permissions.value.createCampus));

        return {
            toggleBankDetailsLock,
            updateBankDetails,
            sendPlatformAgreement,
            view,
            show,
            update,
            updateAssessmentLevel,
            updateAcceptingPartnerRequests,
            updateCriticalDetails,
            updateLogo,
            createBranch,
        } as const;
    };

    const policy = computed(() => getPolicy(model.value));

    return { policy, getPolicy };
};
